import { AxiosError } from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  AuthResponseTokens,
  RegisterResponse,
  SignInValues,
  SignUpValues,
  User,
  UpdateUserValues,
} from './AuthTypes';
import { ErrorResponse } from 'store/ReduxTypes';
import { normalizeApiError } from '@utils/errorMessages';
import { clearPersistAndStore } from '@utils/clearPersistAndStore';
import { api } from '@utils/api';
import T from '@data/text/commonTexts.json';

/* ───────────── helpers ───────────── */
const mapProfile = (p: any): User => {
  const userType = p.user_type === 'physical' || p.user_type === 'corporate' ? p.user_type : undefined;

  return {
    id: p.id,
    surname: p.surname,
    first_name: p.first_name,
    second_name: p.second_name,
    birth_date: p.birth_date,
    email: p.email,
    phone: p.phone || '',
    is_physical: p.is_physical ?? userType === 'physical',
    user_type: userType,
    is_actual: p.is_actual ?? true,
    active_policy: p.active_policy,
    account_company: p.account_company ?? null,
    last_order: p.last_order,
    instance_id: p.instance_id ?? 0,
    attempt_num: p.attempt_num ?? null,
    email_verified: p.email_verified ?? false,
    telegram_id: p.telegram_id,
    promo_codes: p.promo_codes ?? null,
    last_survey_completed_at: p.last_survey_completed_at ?? null,
    current_program: p.current_program ?? null,
    can_generate_analysis_recommendations: p.can_generate_analysis_recommendations,
    can_create_lab_order: p.can_create_lab_order,
    missing_profile_fields: p.missing_profile_fields ?? [],
  };
};

export type TelegramAuthPayload = {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number; // unix (seconds)
  hash: string;
};

/* ───────────────── SIGN-IN ───────────────── */
export const signInOperation = createAsyncThunk<
  AuthResponseTokens,
  SignInValues,
  { rejectValue: ErrorResponse }
>('auth/signIn', async (credentials, { rejectWithValue }) => {
  try {
    const { data } = await api.post<AuthResponseTokens>(
      '/auth/login',
      credentials,
      { withCredentials: true }
    );
    return data;
  } catch (err) {
    const e = err as AxiosError<ErrorResponse>;
    if (e.code === 'ECONNABORTED' || !e.response) {
      return rejectWithValue({ message: T.errors.networkError });
    }
    const msg =
      e.response?.status === 400
        ? T.errors.invalidCredentials
        : e.response?.data?.message || e.response?.data?.detail || e.message;
    return rejectWithValue({ message: msg });
  }
});

/* ───────────────── SIGN-UP ───────────────── */
export const signUpOperation = createAsyncThunk<
  RegisterResponse,
  SignUpValues,
  { rejectValue: ErrorResponse }
>('auth/signUp', async (values, { rejectWithValue }) => {
  try {
    const { data } = await api.post<RegisterResponse>(
      '/auth/register',
      values,
      { withCredentials: true }
    );
    return data;
  } catch (err) {
    return rejectWithValue(normalizeApiError(err));
  }
});

/* ───────────────── SIGN-OUT ───────────────── */
export const signOutOperation = createAsyncThunk<
  void,
  void,
  { rejectValue: ErrorResponse }
>('auth/signOut', async (_, { rejectWithValue }) => {
  try {
    await api.post('/auth/logout', {}, { withCredentials: true });
    await clearPersistAndStore();
  } catch (err) {
    return rejectWithValue(normalizeApiError(err));
  }
});

/* ───────────────── REFRESH ───────────────── */
export const refreshTokenOperation = createAsyncThunk<
  AuthResponseTokens,
  void,
  { rejectValue: ErrorResponse }
>('auth/refresh', async (_, { rejectWithValue }) => {
  try {
    const { data } = await api.post<AuthResponseTokens>(
      '/auth/refresh',
      {},
      { withCredentials: true }
    );
    return data;
  } catch (err) {
    const e = err as AxiosError<ErrorResponse>;
    return rejectWithValue({ message: e.response?.data?.message || e.message });
  }
});

/* ───────────────── GET /me ───────────────── */
export const getUserOperation = createAsyncThunk<User, void, { rejectValue: ErrorResponse }>(
  'auth/getMe',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/auth/me');
      return mapProfile(data);
    } catch (err) {
      const e = err as AxiosError<ErrorResponse>;
      return rejectWithValue({ message: e.response?.data?.message || e.message });
    }
  },
);

/* ───────────────── PUT /me ───────────────── */
export const updateUserOperation = createAsyncThunk<
  User,
  UpdateUserValues,
  { rejectValue: ErrorResponse }
>('auth/updateUser', async (userData, { rejectWithValue }) => {
  try {
    const { data } = await api.patch('/auth/account', userData);
    return mapProfile(data);
  } catch (err) {
    const e = err as AxiosError<any>;
    const respData = e.response?.data as { detail?: string; message?: string };

    return rejectWithValue({
      message: respData?.detail || respData?.message || e.message,
    });
  }
});

/* ───────────────── POST /resend-verification ───────────────── */
export const resendVerificationEmailOperation = createAsyncThunk<
  { message: string },
  string,
  { rejectValue: ErrorResponse }
>('auth/resendVerificationEmail', async (email, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/auth/resend_verification', { email });
    return data;
  } catch (err) {
    const e = err as AxiosError<ErrorResponse>;
    return rejectWithValue({
      message: e.response?.data?.message || e.response?.data?.detail || e.message,
    });
  }
});

/* ───────────────── POST /auth/password/forgot ───────────────── */
export const forgotPasswordOperation = createAsyncThunk<
  { message: string },
  string,
  { rejectValue: ErrorResponse }
>('auth/forgotPassword', async (email, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/auth/password/forgot', { email });
    return data;
  } catch (err) {
    const e = err as AxiosError<ErrorResponse>;
    return rejectWithValue({
      message: e.response?.data?.message || e.response?.data?.detail || e.message,
    });
  }
});

/* ───────────────── POST /auth/password/reset ───────────────── */
export const resetPasswordOperation = createAsyncThunk<
  { message: string },
  { token: string; new_password: string },
  { rejectValue: ErrorResponse }
>('auth/resetPassword', async (payload, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/auth/password/reset', payload, {
      headers: {
        // reset по email-токену должен идти без влияния на текущую auth-сессию
        'X-Skip-Auth': '1',
        'X-Skip-Auth-Retry': '1',
      },
    });
    return data;
  } catch (err) {
    return rejectWithValue(normalizeApiError(err));
  }
});

/* ───────────── CHANGE PASSWORD ───────────── */
export const changePasswordOperation = createAsyncThunk<
  void,
  { old_password: string; new_password: string },
  { rejectValue: ErrorResponse }
>('auth/changePassword', async (payload, { rejectWithValue }) => {
  try {
    await api.post('/auth/change_password', payload);
  } catch (err) {
    const e = err as AxiosError<ErrorResponse>;
    let msg: string;

    if (e.response?.status === 400) {
      msg = (e.response?.data as any)?.detail || T.errors.invalidData;
    } else {
      msg = e.response?.data?.message || (e.response?.data as any)?.detail || e.message;
    }

    return rejectWithValue({ message: msg });
  }
});

/* ───────────── TELEGRAM ───────────── */
export const bindTelegramOperation = createAsyncThunk<
  User,
  TelegramAuthPayload,
  { rejectValue: ErrorResponse }
>('auth/bindTelegram', async (payload, { rejectWithValue }) => {
  try {
    const { data } = await api.post<User>('/auth/telegram', payload, {
      headers: { 'Content-Type': 'application/json' },
      transformRequest: [d => JSON.stringify(d)],
    });
    return data;
  } catch (err) {
    const e = err as AxiosError<ErrorResponse>;
    return rejectWithValue({
      message: e.response?.data?.message || e.response?.data?.detail || e.message,
    });
  }
});
